//frontend/src/components/user/UpdatePassword.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePassword as updatePasswordAction, clearAuthError } from '../../actions/userActions';
import { toast } from 'react-toastify';
import { Lock, Eye, EyeOff, Save } from 'lucide-react';

export default function UpdatePassword() {
    const [formData, setFormData] = useState({
        oldPassword: '',
        password: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        old: false,
        new: false,
        confirm: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useDispatch();
    const { isUpdated, error } = useSelector(state => state.authState);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateForm = () => {
        if (formData.password !== formData.confirmPassword) {
            toast.error('New passwords do not match');
            return false;
        }
        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters long');
            return false;
        }
        return true;
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setIsSubmitting(true);

        const submitData = new FormData();
        submitData.append('oldPassword', formData.oldPassword);
        submitData.append('password', formData.password);

        await dispatch(updatePasswordAction(submitData));
        setIsSubmitting(false);
    };

    useEffect(() => {
        if (isUpdated) {
            toast.success('Password updated successfully');
            setFormData({
                oldPassword: '',
                password: '',
                confirmPassword: ''
            });
        }

        if (error) {
            toast.error(error);
            dispatch(clearAuthError());
        }
    }, [isUpdated, error, dispatch]);

    const renderPasswordInput = (id, label, name, value, showPassword, placeholder) => (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
                {label}
            </label>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    type={showPassword ? "text" : "password"}
                    id={id}
                    name={name}
                    className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    required
                />
                <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => togglePasswordVisibility(name === 'oldPassword' ? 'old' : name === 'password' ? 'new' : 'confirm')}
                >
                    {showPassword ? 
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" /> :
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    }
                </button>
            </div>
        </div>
    );

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                    <h2 className="text-2xl font-bold text-[#dc2626] mb-6 font-['Crimson_Text'] text-center">
                        Update Password
                    </h2>

                    <form onSubmit={submitHandler} className="space-y-6">
                        {renderPasswordInput(
                            "old_password_field",
                            "Current Password",
                            "oldPassword",
                            formData.oldPassword,
                            showPasswords.old,
                            "Enter your current password"
                        )}

                        {renderPasswordInput(
                            "new_password_field",
                            "New Password",
                            "password",
                            formData.password,
                            showPasswords.new,
                            "Enter your new password"
                        )}

                        {renderPasswordInput(
                            "confirm_password_field",
                            "Confirm New Password",
                            "confirmPassword",
                            formData.confirmPassword,
                            showPasswords.confirm,
                            "Confirm your new password"
                        )}

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            <Save className="h-5 w-5 mr-2" />
                            {isSubmitting ? 'Updating...' : 'Update Password'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}